import { PromptInput, PromptInputRef } from '@/components/input/PromptInput';
import { ChatLimitProgress } from '@/components/feedback/ChatLimitProgress';
import { AgeGroup } from '@/constants';
import { useFileUpload, createAttachedFileInfos } from '@/hooks/useFileUpload';
import { AttachedFileInfo, ChatLimitInfo, FileAttachment } from '@/types';
import { useState, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from '@chakra-ui/react';

interface ChatInputProps {
  onSend: (message: string, attachedFileIds?: string[]) => void;
  disabled: boolean;
  ageGroup: AgeGroup;
  sessionId: string;
  chatUsage?: ChatLimitInfo;
  onStartUploading?: (userText: string, attachedFiles?: AttachedFileInfo[]) => void;
  droppedFiles?: FileAttachment[];
  onDroppedFilesProcessed?: () => void;
  onFileError?: (error: string) => void;
}

export const ChatInput = forwardRef<PromptInputRef, ChatInputProps>(
  (
    {
      onSend,
      disabled,
      ageGroup,
      sessionId,
      chatUsage,
      onStartUploading,
      droppedFiles,
      onDroppedFilesProcessed,
      onFileError,
    },
    ref
  ) => {
    const [input, setInput] = useState('');
    const { t } = useTranslation();

    // ファイルアップロードフック
    const { isUploading, uploadAttachedFiles } = useFileUpload({ sessionId });

    const handleSubmit = async (inputValue: string, attachedFiles?: FileAttachment[]) => {
      if ((!inputValue.trim() && !attachedFiles?.length) || disabled || isUploading) return;

      try {
        let fileIds: string[] = [];

        // ファイルがある場合はアップロード
        if (attachedFiles && attachedFiles.length > 0) {
          // 添付ファイル情報を作成（表示用）
          const attachedFileInfos = createAttachedFileInfos(attachedFiles);

          // ユーザーメッセージとAIのローディング表示を開始
          onStartUploading?.(inputValue, attachedFileInfos);

          fileIds = await uploadAttachedFiles(attachedFiles);
        }

        // メッセージを送信
        onSend(inputValue, fileIds.length > 0 ? fileIds : undefined);
      } catch {
        // エラーハンドリング - ファイルアップロード失敗時もメッセージは送信
        onSend(inputValue);
      }
    };

    // 言語レベル別のプレースホルダー
    const getPlaceholder = (): string => {
      switch (ageGroup) {
        case AgeGroup.PRESCHOOL:
          return t('chat.placeholder.preschool');
        case AgeGroup.ELEMENTARY:
          return t('chat.placeholder.elementary');
        case AgeGroup.JUNIOR_HIGH:
        case AgeGroup.HIGH_SCHOOL:
        default:
          return t('chat.placeholder.teen');
      }
    };

    return (
      <Flex w="full" flexDirection="column" alignItems="center" gap={2}>
        {chatUsage && chatUsage.remaining !== null && chatUsage.limit !== null && (
          <ChatLimitProgress remaining={chatUsage.remaining} limit={chatUsage.limit} />
        )}

        <PromptInput
          ref={ref}
          value={input}
          onChange={setInput}
          onSubmit={handleSubmit}
          placeholder={getPlaceholder()}
          disabled={disabled}
          buttonTitle={isUploading ? t('chat.uploading') : t('chat.send')}
          maxLength={1000}
          useTextarea={true}
          enableFileAttachment={true}
          maxFiles={5}
          isUploading={isUploading}
          droppedFiles={droppedFiles}
          onDroppedFilesProcessed={onDroppedFilesProcessed}
          onFileError={onFileError}
        />
      </Flex>
    );
  }
);

ChatInput.displayName = 'ChatInput';
